/**
 * Event Overlap Detection and Resolution
 * Handles vertical stacking of overlapping events in horizontal calendar views
 */

import type { CalendarEvent } from '@/api/types/calendars.types';

export interface PositionedEvent {
  event: CalendarEvent;
  left: number;
  width: number;
  top: number;
  height: number;
  layer: number;
  totalLayers: number;
}

export interface EventPosition {
  left: number;
  width: number;
  startsBeforeView: boolean;
  endsAfterView: boolean;
}

/**
 * Detect if two events overlap in time
 */
export const doEventsOverlap = (
  event1Start: Date,
  event1End: Date,
  event2Start: Date,
  event2End: Date
): boolean => {
  // Events overlap if one starts before the other ends
  return event1Start < event2End && event2Start < event1End;
};

/**
 * Group overlapping events into layers for vertical stacking
 * Smart layout: only creates layers for events that actually overlap
 */
export const calculateEventLayers = (
  events: Array<{ event: CalendarEvent; position: EventPosition }>,
  parse: (date: string) => Date
): PositionedEvent[] => {
  if (events.length === 0) return [];

  // Sort events by start time, then by duration (longer first)
  const sorted = [...events].sort((a, b) => {
    const aStart = parse(a.event.start_time);
    const bStart = parse(b.event.start_time);
    if (aStart.getTime() !== bStart.getTime()) {
      return aStart.getTime() - bStart.getTime();
    }
    // If same start time, longer events first
    const aDuration = parse(a.event.end_time).getTime() - aStart.getTime();
    const bDuration = parse(b.event.end_time).getTime() - bStart.getTime();
    return bDuration - aDuration;
  });

  // Create groups of overlapping events
  const overlapGroups: Array<
    Array<{
      event: CalendarEvent;
      position: EventPosition;
      start: Date;
      end: Date;
    }>
  > = [];

  sorted.forEach(({ event, position }) => {
    const start = parse(event.start_time);
    const end = parse(event.end_time);

    // Find a group this event overlaps with
    let addedToGroup = false;
    for (const group of overlapGroups) {
      // Check if this event overlaps with any event in the group
      const overlapsWithGroup = group.some((item) =>
        doEventsOverlap(start, end, item.start, item.end)
      );

      if (overlapsWithGroup) {
        group.push({ event, position, start, end });
        addedToGroup = true;
        break;
      }
    }

    // If event doesn't overlap with any existing group, create a new group
    if (!addedToGroup) {
      overlapGroups.push([{ event, position, start, end }]);
    }
  });

  const result: PositionedEvent[] = [];
  const rowHeight = 64; // Total height available for events in a row
  const eventGap = 2; // Gap between stacked events
  const topPadding = 8; // Top padding for events

  // Process each group
  overlapGroups.forEach((group) => {
    if (group.length === 1) {
      // Single event - gets full height
      const { event, position } = group[0];
      result.push({
        event,
        left: position.left,
        width: position.width,
        top: topPadding,
        height: rowHeight - topPadding - topPadding, // Full height minus top/bottom padding
        layer: 0,
        totalLayers: 1,
      });
    } else {
      // Multiple overlapping events - arrange in layers
      const layers: Array<Array<(typeof group)[0]>> = [];

      group.forEach((item) => {
        // Find first layer where this event doesn't overlap
        let layerIndex = 0;
        let placed = false;

        while (!placed) {
          if (!layers[layerIndex]) {
            layers[layerIndex] = [];
          }

          // Check if event overlaps with any event in this layer
          const overlaps = layers[layerIndex].some((existing) =>
            doEventsOverlap(item.start, item.end, existing.start, existing.end)
          );

          if (!overlaps) {
            layers[layerIndex].push(item);
            placed = true;
          } else {
            layerIndex++;
          }
        }
      });

      // Calculate height for each layer
      const totalLayers = layers.length;
      const eventHeight =
        (rowHeight - topPadding * 2 - (totalLayers - 1) * eventGap) /
        totalLayers;

      // Add positioned events from all layers
      layers.forEach((layer, layerIndex) => {
        layer.forEach(({ event, position }) => {
          result.push({
            event,
            left: position.left,
            width: position.width,
            top: topPadding + layerIndex * (eventHeight + eventGap),
            height: eventHeight,
            layer: layerIndex,
            totalLayers,
          });
        });
      });
    }
  });

  return result;
};

/**
 * Simple layout: place events one below another without overlap detection
 * Used when overlap detection is disabled or for simpler displays
 */
export const calculateSimpleLayout = (
  events: Array<{ event: CalendarEvent; position: EventPosition }>
): PositionedEvent[] => {
  const eventHeight = 60;
  const topPadding = 8;

  return events.map(({ event, position }) => ({
    event,
    left: position.left,
    width: position.width,
    top: topPadding,
    height: eventHeight,
    layer: 0,
    totalLayers: 1,
  }));
};
